import { msg } from 'gt-react';
import {
  createRoute,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { Action, Subject } from '@erp/authz';
import { ProductModule } from '@erp/shared-types';
import { ReceiptIcon } from '@phosphor-icons/react';
import { fetchEnhancedSalesOrderUiEnabled } from '@/api/org/config/useEnhancedSalesOrderUi';
import {
  Route as pathlessAuthenticatedRoute,
  requireModules,
  requirePermission,
} from '../route';

// OMS v3 owns the unversioned path. Its page components intentionally stay in
// ../sales-orders-v3/ (folder not renamed to avoid churn); the legacy Sales
// Orders page components live beside this module and are served at
// /sales-orders-v2.
const SalesOrdersV3Page = lazyRouteComponent(
  () => import('../sales-orders-v3/SalesOrdersV3Page'),
);

export const Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/sales-orders',
  staticData: {
    app: {
      nav: {
        label: msg('Sales Orders', {
          context: 'sidebar nav label, Sales section',
        }),
        icon: <ReceiptIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 20,
      },
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.SalesOrderDraftV3 },
        ],
      },
    },
  },
  component: SalesOrdersV3Page,
  // Persist pagination AND filters in the URL so a refresh / shared link /
  // browser-back from an order returns to the same view. `salesOrderId` is a
  // legacy v2 deep-link param declared only so it survives the legacy-org
  // redirect below (zod strips undeclared keys before beforeLoad runs).
  // Multi-value filters are comma-joined strings, NOT arrays — the router
  // JSON-serializes arrays into unreadable `%5B%22…%22%5D` URLs. The values
  // stay loose — the list endpoint validates them and an unknown value just
  // filters to nothing; `.catch(undefined)` keeps a hand-mangled URL from
  // erroring the route.
  validateSearch: z.object({
    page: z.coerce.number().int().min(1).optional(),
    pageSize: z.coerce.number().int().min(1).max(100).optional(),
    salesOrderId: z.string().optional(),
    customerIds: z.string().optional().catch(undefined),
    excludedCustomerIds: z.string().optional().catch(undefined),
    // Lifecycle stages on the v3 page (the Stage column's vocabulary).
    statuses: z.string().optional().catch(undefined),
    sources: z.string().optional().catch(undefined),
    purchaseOrderNumber: z.string().optional().catch(undefined),
    // Orders for the customers a person is assigned (Members → Data Access →
    // "View sales orders"); resolved server-side by `salesOrdersV3.list`.
    assigneeUserId: z.string().optional().catch(undefined),
    // Inclusive `receivedDate` bounds, ISO `YYYY-MM-DD` (validated by the API).
    receivedFrom: z.string().optional().catch(undefined),
    receivedTo: z.string().optional().catch(undefined),
    // Only orders submitted to the ERP whose order number is still pending
    // (the ERP Order # column's "Pending" rows). A boolean, like
    // product-catalog's `noPriceList`: the router's JSON search parser would
    // turn a `'1'` string literal into the number 1 and drop it.
    erpPending: z.boolean().optional().catch(undefined),
  }),
  loader: () => ({
    crumb: msg('Sales Orders', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context, search }) => {
    requireModules({
      context,
      oneOf: [ProductModule.ORDER_MANAGEMENT],
    });
    // TEMPORARY redirect — remove after all tenants migrate to OMS v3.
    // Legacy orgs keep the old pages: redirect BEFORE the v3 permission check
    // so a user without SalesOrderDraftV3 lands on /sales-orders-v2, not
    // /unauthorized.
    if (!(await fetchEnhancedSalesOrderUiEnabled(context.queryClient))) {
      throw redirect({ to: '/sales-orders-v2', search });
    }
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.SalesOrderDraftV3,
    });
  },
});
